import React, { useState, useEffect } from 'react';
import { Instagram, Image, MessageSquare, RefreshCw, Check, X, ChevronRight, Eye, Heart, MessageCircle, Send, Copy, Settings, Loader2, AlertCircle } from 'lucide-react';

interface InstagramPost {
  id: string;
  caption: string;
  media_url: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  timestamp: string;
  permalink: string;
  like_count?: number;
  comments_count?: number;
  thumbnail_url?: string;
}

interface ResponseTemplate {
  id: string;
  name: string;
  trigger: 'specific_post' | 'any_post' | 'keyword';
  postId?: string;
  keywords?: string[];
  responses: {
    en: string;
    pt: string;
  };
  includeLink: boolean;
  linkUrl?: string;
  linkText?: string;
  active: boolean;
  created: string;
  modified: string;
}

export const InstagramPostResponder: React.FC = () => {
  const [responseMode, setResponseMode] = useState<'specific' | 'any'>('specific');
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<InstagramPost | null>(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<ResponseTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<ResponseTemplate>({
    id: '',
    name: '',
    trigger: 'specific_post',
    responses: { en: '', pt: '' },
    includeLink: true,
    linkUrl: 'https://biig.dev/chat',
    linkText: 'Chat with us',
    active: true,
    created: new Date().toISOString(),
    modified: new Date().toISOString()
  });
  const [language, setLanguage] = useState<'en' | 'pt'>('en');
  const [previewMode, setPreviewMode] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load saved templates
  useEffect(() => {
    // Try to load from backend first
    fetch('/api/instagram/posts/templates/load')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.templates) {
          setTemplates(data.templates);
          localStorage.setItem('instagram-response-templates', JSON.stringify(data.templates));
        }
      })
      .catch(() => {
        // Fallback to localStorage
        const saved = localStorage.getItem('instagram-response-templates');
        if (saved) {
          setTemplates(JSON.parse(saved));
        }
      });
    
    // Load access token from integration checklist
    const envVars = localStorage.getItem('integration-env-vars-v2');
    if (envVars) {
      const vars = JSON.parse(envVars);
      const token = vars.find((v: any) => v.key === 'INSTAGRAM_ACCESS_TOKEN');
      if (token?.value) {
        setAccessToken(token.value);
      }
    }
  }, []);

  // Fetch Instagram posts
  const fetchPosts = async () => {
    if (!accessToken) {
      setError('Instagram Access Token not configured. Please set it up in /setup');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Try to fetch real posts from API
      const response = await fetch(`/api/instagram/posts/list?access_token=${accessToken}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          setPosts(data.data);
          return;
        }
      }
      
      // Fallback to mock data if API fails
      const mockPosts: InstagramPost[] = [
        {
          id: '1',
          caption: '🌴 Welcome to Paradise! Enjoy your stay at Carneiros Paradiso 🏖️',
          media_url: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=400',
          media_type: 'IMAGE',
          timestamp: '2024-03-15T10:00:00Z',
          permalink: 'https://instagram.com/p/abc123',
          like_count: 245,
          comments_count: 18
        },
        {
          id: '2',
          caption: 'Sunset views from our beachfront restaurant 🌅🍽️',
          media_url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400',
          media_type: 'IMAGE',
          timestamp: '2024-03-14T18:30:00Z',
          permalink: 'https://instagram.com/p/def456',
          like_count: 389,
          comments_count: 27
        },
        {
          id: '3',
          caption: 'Special weekend packages available! Book now 📅✨',
          media_url: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400',
          media_type: 'IMAGE',
          timestamp: '2024-03-13T14:00:00Z',
          permalink: 'https://instagram.com/p/ghi789',
          like_count: 156,
          comments_count: 12
        },
        {
          id: '4',
          caption: 'Morning yoga sessions on the beach 🧘‍♀️🌊',
          media_url: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=400',
          media_type: 'IMAGE',
          timestamp: '2024-03-12T07:00:00Z',
          permalink: 'https://instagram.com/p/jkl012',
          like_count: 298,
          comments_count: 22
        }
      ];
      
      setPosts(mockPosts);
      
      // Real API call would be:
      // const response = await fetch(`https://graph.facebook.com/v18.0/me/media?fields=id,caption,media_url,media_type,timestamp,permalink&access_token=${accessToken}`);
      // const data = await response.json();
      // setPosts(data.data);
      
    } catch (err) {
      setError('Failed to fetch Instagram posts. Please check your access token.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Save template
  const saveTemplate = () => {
    const newTemplate: ResponseTemplate = {
      ...currentTemplate,
      id: currentTemplate.id || Date.now().toString(),
      postId: selectedPost?.id,
      trigger: responseMode === 'specific' ? 'specific_post' : 'any_post',
      modified: new Date().toISOString()
    };

    const updatedTemplates = currentTemplate.id
      ? templates.map(t => t.id === currentTemplate.id ? newTemplate : t)
      : [...templates, newTemplate];

    setTemplates(updatedTemplates);
    localStorage.setItem('instagram-response-templates', JSON.stringify(updatedTemplates));
    
    // Also save to backend
    fetch('/api/instagram/posts/templates/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templates: updatedTemplates, updatedBy: 'User' })
    }).catch(err => console.error('Failed to save to backend:', err));
    
    // Reset form
    setCurrentTemplate({
      id: '',
      name: '',
      trigger: 'specific_post',
      responses: { en: '', pt: '' },
      includeLink: true,
      linkUrl: 'https://biig.dev/chat',
      linkText: 'Chat with us',
      active: true,
      created: new Date().toISOString(),
      modified: new Date().toISOString()
    });
    setSelectedPost(null);
  };

  // Format response with variables
  const formatResponse = (template: string, post?: InstagramPost) => {
    let formatted = template;
    
    // Replace variables
    formatted = formatted.replace('{username}', '@user');
    formatted = formatted.replace('{post_caption}', post?.caption || 'your post');
    formatted = formatted.replace('{business_name}', 'Carneiros Paradiso');
    
    if (currentTemplate.includeLink) {
      formatted += `\n\n🔗 ${currentTemplate.linkText}: ${currentTemplate.linkUrl}`;
    }
    
    return formatted;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                <Instagram className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Instagram Post Responder
                </h1>
                <p className="text-gray-600">Create automated responses for specific posts or all posts</p>
              </div>
            </div>
            
            <button
              onClick={fetchPosts}
              disabled={loading}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 flex items-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Load Posts
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-600">{error}</span>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          
          {/* Left: Post Selection */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-4">1. Choose Response Type</h2>
            
            {/* Response Mode Toggle */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setResponseMode('specific')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                  responseMode === 'specific'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Specific Post
              </button>
              <button
                onClick={() => setResponseMode('any')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                  responseMode === 'any'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <MessageCircle className="w-4 h-4 inline mr-2" />
                Any Post
              </button>
            </div>

            {responseMode === 'specific' && (
              <>
                <h3 className="font-medium mb-3">Select a Post:</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {posts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No posts loaded yet</p>
                      <p className="text-sm">Click "Load Posts" to fetch your Instagram posts</p>
                    </div>
                  ) : (
                    posts.map(post => (
                      <div
                        key={post.id}
                        onClick={() => setSelectedPost(post)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedPost?.id === post.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex gap-3">
                          <img
                            src={post.media_url}
                            alt="Post"
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium line-clamp-2">{post.caption}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" />
                                {post.like_count || 0}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                {post.comments_count || 0}
                              </span>
                              <span>{new Date(post.timestamp).toLocaleDateString()}</span>
                            </div>
                          </div>
                          {selectedPost?.id === post.id && (
                            <Check className="w-5 h-5 text-purple-500" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {responseMode === 'any' && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  This template will be used for all posts that don't have a specific template assigned.
                </p>
              </div>
            )}
          </div>

          {/* Right: Response Template */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-4">2. Create Response Template</h2>
            
            {/* Template Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={currentTemplate.name}
                onChange={(e) => setCurrentTemplate({ ...currentTemplate, name: e.target.value })}
                placeholder="e.g., Welcome Message, Beach Photo Response"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Language Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setLanguage('en')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                  language === 'en'
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                    : 'bg-gray-50 text-gray-600 border-2 border-gray-200'
                }`}
              >
                🇬🇧 English
              </button>
              <button
                onClick={() => setLanguage('pt')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                  language === 'pt'
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                    : 'bg-gray-50 text-gray-600 border-2 border-gray-200'
                }`}
              >
                🇧🇷 Português
              </button>
            </div>

            {/* Response Text */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Response Message ({language === 'en' ? 'English' : 'Portuguese'})
              </label>
              <textarea
                value={currentTemplate.responses[language]}
                onChange={(e) => setCurrentTemplate({
                  ...currentTemplate,
                  responses: { ...currentTemplate.responses, [language]: e.target.value }
                })}
                placeholder={language === 'en' 
                  ? "Hi {username}! Thanks for your interest in our resort. We'd love to help you plan your perfect stay!"
                  : "Olá {username}! Obrigado pelo interesse em nosso resort. Adoraríamos ajudar você a planejar sua estadia perfeita!"
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <div className="mt-1 text-xs text-gray-500">
                Available variables: {'{username}'}, {'{post_caption}'}, {'{business_name}'}
              </div>
            </div>

            {/* Link Settings */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={currentTemplate.includeLink}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, includeLink: e.target.checked })}
                  className="rounded text-purple-500"
                />
                <span className="text-sm font-medium">Include link in response</span>
              </label>
              
              {currentTemplate.includeLink && (
                <div className="ml-6 space-y-2">
                  <input
                    type="text"
                    value={currentTemplate.linkText}
                    onChange={(e) => setCurrentTemplate({ ...currentTemplate, linkText: e.target.value })}
                    placeholder="Link text"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                  <input
                    type="url"
                    value={currentTemplate.linkUrl}
                    onChange={(e) => setCurrentTemplate({ ...currentTemplate, linkUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                </div>
              )}
            </div>

            {/* Preview */}
            {(currentTemplate.responses.en || currentTemplate.responses.pt) && (
              <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700">Preview</h4>
                  <button
                    onClick={() => setPreviewMode(!previewMode)}
                    className="text-xs text-purple-600 hover:text-purple-700"
                  >
                    <Eye className="w-4 h-4 inline mr-1" />
                    {previewMode ? 'Hide' : 'Show'} Preview
                  </button>
                </div>
                
                {previewMode && (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {formatResponse(currentTemplate.responses[language], selectedPost || undefined)}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={saveTemplate}
                disabled={!currentTemplate.name || (!currentTemplate.responses.en && !currentTemplate.responses.pt)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
                  currentTemplate.name && (currentTemplate.responses.en || currentTemplate.responses.pt)
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Check className="w-4 h-4" />
                Save Template
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(formatResponse(currentTemplate.responses[language], selectedPost || undefined));
                }}
                className="px-4 py-2 border-2 border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
          </div>
        </div>

        {/* Saved Templates */}
        {templates.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
            <h2 className="text-lg font-semibold mb-4">Saved Templates</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <div
                  key={template.id}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium">{template.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      template.active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {template.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {template.trigger === 'specific_post' ? 'Specific Post' : 'Any Post'}
                  </p>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {template.responses.en || template.responses.pt}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        setCurrentTemplate(template);
                        if (template.postId) {
                          const post = posts.find(p => p.id === template.postId);
                          setSelectedPost(post || null);
                        }
                      }}
                      className="text-xs text-purple-600 hover:text-purple-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        const updated = templates.filter(t => t.id !== template.id);
                        setTemplates(updated);
                        localStorage.setItem('instagram-response-templates', JSON.stringify(updated));
                      }}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};